import {groupBy} from '@benchling/js-utils';

import {assert} from 'Asserts';
import {SchemaTag} from 'folder-items/FileConstants';
import {ProcedureParameterConfirmationMethod} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getAccessibleOrInaccessibleId, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  getPowAgnosticLatestFlowchartVersionFromProcedure,
  getPowAgnosticProcedureFromFlowchartVersion,
  getPowAgnosticTemplateFlowchartVersionFromProcedureRun,
} from 'procedures/utils/powAgnosticFlowchartHelpers';
import {
  MethodDefinitionVersionTestOrderConfig,
  PlateDataParameter,
  PlateMaterialInput,
  PlateMapProcedureContext,
  PlateProcedureParameterType,
  SUPPORTED_FIELD_TYPES_FOR_MATERIAL_INPUTS,
} from 'samples/plate-maps/PlateMapConstants';

import {ParametersForPlateMapInConfigQuery} from './__generated/ParametersForPlateMapInConfigQuery';
import {ParametersForPlateMapInExecutionQuery} from './__generated/ParametersForPlateMapInExecutionQuery';
import {ProcedureMethodDefinitionVersionForPlateMapGraphQL} from './__generated/ProcedureMethodDefinitionVersionForPlateMapGraphQL';

export type ExecutionTransformResult = PlateMapProcedureContext & {
  procedureMethodExecutionInstanceId: string | null;
};

export function transformExecutionQueryData(
  queryData: ParametersForPlateMapInExecutionQuery,
  currentStepExecutionInstanceId: string
): ExecutionTransformResult {
  assert(
    !isInaccessible(queryData.procedureStepExecutionInstance),
    'Expecting step execution instance to be accessible'
  );
  const pmei = queryData.procedureStepExecutionInstance.methodExecutionInstance;
  if (isInaccessible(pmei.planMethodDefinitionVersion)) {
    return {
      isInProcedure: true,
      procedureStudyDesignType: null,
      procedureId: null,
      procedureMethodExecutionInstanceId: null,
    };
  }
  const planVersion = pmei.planMethodDefinitionVersion;

  const inCurrentStepMap = pmei.parameterUsagesExpensive.reduce(
    (acc: {[parameterVersionId: string]: boolean}, {stepExecutionInstanceId, parameterVersionId}) => {
      acc[parameterVersionId] ??= false;
      acc[parameterVersionId] ||= stepExecutionInstanceId === currentStepExecutionInstanceId;
      return acc;
    },
    {}
  );

  const confirmationMethodByParameterIdMap = new Map<string, ProcedureParameterConfirmationMethod>(
    pmei.parameterConfirmationMethods.map(({parameterId, confirmationMethod}) => [
      parameterId,
      confirmationMethod,
    ])
  );

  const plateUsedGQLMaterialInputs = planVersion.materialInputSettings.filter(
    ({version}) =>
      confirmationMethodByParameterIdMap.get(version.procedureMaterialInputId) ===
      ProcedureParameterConfirmationMethod.PLATE_MAP
  );

  assert(
    plateUsedGQLMaterialInputs.every(
      ({
        version: {
          restMaterialField: {fieldType},
        },
      }) => SUPPORTED_FIELD_TYPES_FOR_MATERIAL_INPUTS.has(fieldType)
    ),
    'Expected plate map confirmed material inputs to have valid data type'
  );

  const plateMaterialInputValuesByParameterVersionId = groupBy(
    pmei.materialInputPlannedValues,
    (value) => value.parameterVersion.id
  );
  const plateDataParamValuesByParameterVersionId = groupBy(
    pmei.dataParameterPlannedValues,
    (value) => value.parameterVersion.id
  );

  const plateDataParameters = planVersion.dataParameterSettings
    .filter(
      ({version}) =>
        confirmationMethodByParameterIdMap.get(version.procedureDataParameterId) ===
        ProcedureParameterConfirmationMethod.PLATE_MAP
    )
    .map<PlateDataParameter>(({version}) => ({
      type: PlateProcedureParameterType.DATA_PARAMETER,
      versionId: version.id,
      name: version.name,
      schemaField: version.restValueField,
      plannedValues: (plateDataParamValuesByParameterVersionId[version.id] ?? []).map(
        ({value}: {value: Omit<SchemaTag, 'name' | 'tagSchemaField'>}) => ({
          ...value,
          tagSchemaField: version.restValueField,
          name: version.restValueField.name,
        })
      ),
      inStep: inCurrentStepMap[version.id] ?? false,
    }));

  const plateMaterialInputs = plateUsedGQLMaterialInputs.map<PlateMaterialInput>(({version}) => ({
    type: PlateProcedureParameterType.MATERIAL_INPUT,
    versionId: version.id,
    name: version.name,
    schemaField: version.restMaterialField,
    plannedValues: (plateMaterialInputValuesByParameterVersionId[version.id] ?? []).map(
      ({material}: {material: Omit<SchemaTag, 'name' | 'tagSchemaField'>}) => ({
        ...material,
        tagSchemaField: version.restMaterialField,
        name: version.restMaterialField.name,
      })
    ),
    inStep: inCurrentStepMap[version.id] ?? false,
  }));

  const procedureStudyDesignType =
    pmei.procedureRun.study && !isInaccessible(pmei.procedureRun.study)
      ? (pmei.procedureRun.study.studyDesign?.type ?? null)
      : null;

  const flowchartVersion = getPowAgnosticTemplateFlowchartVersionFromProcedureRun(pmei.procedureRun);
  const procedureParent = !isInaccessible(flowchartVersion)
    ? getPowAgnosticProcedureFromFlowchartVersion(flowchartVersion)
    : null;
  const procedureId = procedureParent != null ? procedureParent.id : null;

  const procedureTestOrderConfig: MethodDefinitionVersionTestOrderConfig = {
    testDefinitionIds: planVersion.testDefinitions.map((testDef) => getAccessibleOrInaccessibleId(testDef)),
    allowedDilutionFactors: planVersion.dilutionPlan.allowedDilutionFactors,
  };

  return {
    isInProcedure: true,
    plateDataParameters,
    plateMaterialInputs,
    procedureStudyDesignType,
    procedureId,
    procedureTestOrderConfig,
    procedureMethodExecutionInstanceId: pmei.id,
  };
}

export type ConfigTransformResult = PlateMapProcedureContext & {
  procedureMethodExecutionInstanceId: null;
};

export function transformConfigQueryData(
  queryData: ParametersForPlateMapInConfigQuery,
  methodVersionId: string,
  stepVersionId: string
): ConfigTransformResult | null {
  assert(
    !isInaccessible(queryData.procedure),
    'Plate map wrapper should only be used with a known accessible procedure'
  );

  const latestFlowchartVersion = getPowAgnosticLatestFlowchartVersionFromProcedure(queryData.procedure);
  assert(latestFlowchartVersion != null, 'Latest flowchart version should not be null');
  assert(
    !isInaccessible(latestFlowchartVersion),
    'Plate map wrapper should only be used with a known accessible procedure flowchart version'
  );

  const methodVersion = latestFlowchartVersion.nodes.find(({id}) => id === methodVersionId);
  if (methodVersion == null) {
    return null;
  }

  const {plateDataParameters, plateMaterialInputs, procedureTestOrderConfig} = transformMethodVersion(
    methodVersion,
    stepVersionId
  );

  return {
    isInProcedure: true,
    procedureId: queryData.procedure.id,
    plateDataParameters,
    plateMaterialInputs,
    procedureTestOrderConfig,
    procedureMethodExecutionInstanceId: null,
    procedureStudyDesignType: null,
  };
}

function transformMethodVersion(
  methodVersion: ProcedureMethodDefinitionVersionForPlateMapGraphQL,
  stepVersionId: string
): {
  plateDataParameters: Array<PlateDataParameter>;
  plateMaterialInputs: Array<PlateMaterialInput>;
  procedureTestOrderConfig: MethodDefinitionVersionTestOrderConfig;
} {
  const inCurrentStepMap = methodVersion.parameterUsagesExpensive.reduce(
    (acc: {[parameterVersionId: string]: boolean}, {stepId, parameterVersionId}) => {
      acc[parameterVersionId] ??= false;
      acc[parameterVersionId] ||= stepId === stepVersionId;
      return acc;
    },
    {}
  );

  const plateUsedGQLMaterialInputs = methodVersion.materialInputSettings.filter(
    ({version: {confirmationMethod}}) => confirmationMethod === ProcedureParameterConfirmationMethod.PLATE_MAP
  );

  assert(
    plateUsedGQLMaterialInputs.every(
      ({
        version: {
          restMaterialField: {fieldType},
        },
      }) => SUPPORTED_FIELD_TYPES_FOR_MATERIAL_INPUTS.has(fieldType)
    ),
    'Expected plate map confirmed material inputs to have valid data type'
  );

  const plateDataParameters = methodVersion.dataParameterSettings
    .filter(
      ({version: {confirmationMethod}}) =>
        confirmationMethod === ProcedureParameterConfirmationMethod.PLATE_MAP
    )
    .map<PlateDataParameter>(({version, values}) => ({
      type: PlateProcedureParameterType.DATA_PARAMETER,
      versionId: version.id,
      name: version.name,
      schemaField: version.restValueField,
      plannedValues: values.map(({value}: {value: Omit<SchemaTag, 'name' | 'tagSchemaField'>}) => ({
        ...value,
        tagSchemaField: version.restValueField,
        name: version.restValueField.name,
      })),
      inStep: inCurrentStepMap[version.id] ?? false,
    }));

  const plateMaterialInputs = plateUsedGQLMaterialInputs.map<PlateMaterialInput>(({version, values}) => ({
    type: PlateProcedureParameterType.MATERIAL_INPUT,
    versionId: version.id,
    name: version.name,
    schemaField: version.restMaterialField,
    plannedValues: values.map(({material}: {material: Omit<SchemaTag, 'name' | 'tagSchemaField'>}) => ({
      ...material,
      tagSchemaField: version.restMaterialField,
      name: version.restMaterialField.name,
    })),
    inStep: inCurrentStepMap[version.id] ?? false,
  }));

  const procedureTestOrderConfig: MethodDefinitionVersionTestOrderConfig = {
    testDefinitionIds: methodVersion.testDefinitions.map((testDef) => getAccessibleOrInaccessibleId(testDef)),
    allowedDilutionFactors: methodVersion.dilutionPlan.allowedDilutionFactors,
  };

  return {plateDataParameters, plateMaterialInputs, procedureTestOrderConfig};
}
